import { formatIsoDate, normalizeDayStart, parseBackendDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { maskCpfCnpj } from '../../utils/maskCpfCnpj';

export const CRM_CLIENT_UNITS_API = 'crm/clientUnits';
export const CRM_CLIENT_CONTACTS_API = 'crm/clientContacts';
export const CRM_CLIENT_PROTOCOLS_API = 'crm/clientProtocols';
export const CRM_CLIENT_FINANCE_API = 'crm/clientFinance';

export const FINANCE_PAGE_SIZE = 50;

export const CRM_CLIENT_EMPTY_CONTACTS = { cliente: null, contatos: [] };
export const CRM_CLIENT_EMPTY_FINANCE = {
    contas_a_receber: { items: [], total: 0, page: 1, page_size: FINANCE_PAGE_SIZE },
    multas: [],
    acordos: []
};

const fetchClientResource = async (api, payload, fallback) => {
    const response = await fetchFlask(api, JSON.stringify(payload));

    return response?.status === 'success' ? response.data : fallback;
};

export const fetchClientUnits = (clienteId) => fetchClientResource(
    CRM_CLIENT_UNITS_API,
    { cliente_id: Number(clienteId) },
    []
);

export const fetchClientContacts = (clienteId) => fetchClientResource(
    CRM_CLIENT_CONTACTS_API,
    { cliente_id: Number(clienteId) },
    CRM_CLIENT_EMPTY_CONTACTS
);

export const fetchClientProtocols = (clienteId) => fetchClientResource(
    CRM_CLIENT_PROTOCOLS_API,
    { cliente_id: Number(clienteId) },
    []
);

export const fetchClientFinance = (clienteId, page = 1, pageSize = FINANCE_PAGE_SIZE) => fetchClientResource(
    CRM_CLIENT_FINANCE_API,
    { cliente_id: Number(clienteId), page, page_size: pageSize },
    CRM_CLIENT_EMPTY_FINANCE
);

export const CRM_CLIENT_TABS = [
    { key: 'lead', label: 'Lead' },
    { key: 'unidades', label: 'Unidades' },
    { key: 'contato', label: 'Contato' },
    { key: 'protocolos', label: 'Protocolos' },
    { key: 'financeiro', label: 'Financeiro' }
];

const normalizeStatus = (value) => String(value ?? '').trim().toUpperCase();

export const RECEIVABLE_LATE_STATUS = 'RECEBIDO COM ATRASO';

// Valores medidos em fReceberOmie/fMultas/fAcordos: RECEBIDO, A VENCER, ATRASADO,
// CANCELADO, EM ACORDO, PARCIAL, EM CONTESTAÇÃO e ATRASADO (EM CONTATO).
const FINANCE_STATUS_TONES = {
    RECEBIDO: 'is-positive',
    [RECEIVABLE_LATE_STATUS]: 'is-warning',
    'A VENCER': 'is-info',
    ATRASADO: 'is-danger',
    'ATRASADO (EM CONTATO)': 'is-danger',
    CANCELADO: 'is-muted',
    'EM ACORDO': 'is-warning',
    PARCIAL: 'is-warning',
    'EM CONTESTAÇÃO': 'is-warning'
};

// Valores medidos em fProtocolos: CONCLUÍDO, ENCERRADO, TN, EMAIL, RECLAMAÇÃO e nulo.
const PROTOCOL_STATUS_TONES = {
    CONCLUÍDO: 'is-positive',
    ENCERRADO: 'is-muted',
    TN: 'is-info',
    EMAIL: 'is-info',
    RECLAMAÇÃO: 'is-warning'
};

export const getFinanceStatusTone = (status) => FINANCE_STATUS_TONES[normalizeStatus(status)] ?? 'is-neutral';

export const getReceivableDisplayStatus = (row) => {
    if (normalizeStatus(row?.status) !== 'RECEBIDO') return row?.status;

    const pagamento = parseBackendDate(row?.pagamento);
    const vencimento = parseBackendDate(row?.vencimento);

    if (!pagamento || !vencimento) return row?.status;

    return normalizeDayStart(pagamento) > normalizeDayStart(vencimento)
        ? RECEIVABLE_LATE_STATUS
        : row?.status;
};

export const getProtocolStatusTone = (status) => PROTOCOL_STATUS_TONES[normalizeStatus(status)] ?? 'is-neutral';

// no_consorcio tem tres estados. null nao e "fora do consorcio": quer dizer que o
// Documento da linha esta vazio, o que vale para 39% das linhas de dCC. Tratar
// null como false rotularia errado quase quatro em cada dez ligacoes.
export const CONSORCIO_SEM_INFORMACAO = { label: 'Consórcio não informado', tone: 'is-muted' };

export const getConsorcioBadge = (noConsorcio) => {
    if (noConsorcio === true) return { label: 'Consórcio', tone: 'is-warning' };
    if (noConsorcio === false) return { label: 'Fora do consórcio', tone: 'is-neutral' };

    return CONSORCIO_SEM_INFORMACAO;
};

// "Em aberto" = tudo que nao foi recebido nem cancelado.
export const isOpenAmount = (status) => {
    const normalized = normalizeStatus(status);

    return normalized !== 'RECEBIDO' && normalized !== 'CANCELADO';
};

export const isOverdueAmount = (status) => normalizeStatus(status).startsWith('ATRASADO');

export const sumWhere = (rows, predicate) => rows
    .filter((row) => predicate(row.status))
    .reduce((total, row) => total + (Number(row.valor) || 0), 0);

export const buildPhoneLabel = (ddd, telefone) => {
    const number = String(telefone ?? '').trim();

    if (!number) return '';

    const areaCode = String(ddd ?? '').trim();

    return areaCode ? `(${areaCode}) ${number}` : number;
};

export const getTotalPages = (total, pageSize) => Math.max(1, Math.ceil((total || 0) / pageSize));

// Multas e acordos vem inteiros do backend, entao da para somar. Contas a receber
// vem paginado: somar os 50 lancamentos da pagina daria um "em aberto" que nao e o
// do cliente, entao aqui so entra a contagem, que o backend manda em total.
export const buildFinanceSummary = (finance) => {
    const receivables = finance?.contas_a_receber ?? CRM_CLIENT_EMPTY_FINANCE.contas_a_receber;
    const penalties = finance?.multas ?? [];
    const agreements = finance?.acordos ?? [];

    return {
        totalLancamentos: receivables.total ?? 0,
        multas: sumWhere(penalties, isOpenAmount),
        acordos: sumWhere(agreements, isOpenAmount),
        totalMultas: penalties.length,
        totalAcordos: agreements.length
    };
};

export const CONTACT_UPDATE_API = 'alterarContato';

export const CONTACT_UPDATE_FIELDS = [
    { key: 'nome', label: 'Nome', span: 'is-medium' },
    { key: 'email', label: 'E-mail', span: 'is-medium' },
    { key: 'emailDois', label: 'E-mail 2', span: 'is-medium' },
    { key: 'ddd', label: 'DDD', span: 'is-tiny' },
    { key: 'telefone', label: 'Telefone', span: 'is-small' },
    { key: 'dddDois', label: 'DDD 2', span: 'is-tiny' },
    { key: 'telefoneDois', label: 'Telefone 2', span: 'is-small' }
];

export const EMPTY_CONTACT_UPDATE_FORM = CONTACT_UPDATE_FIELDS.reduce(
    (form, field) => ({ ...form, [field.key]: '' }),
    {}
);

export const getContactUnitOptions = (contacts) => [
    ...new Set((contacts?.contatos ?? []).map((contato) => contato.unidade).filter(Boolean))
];

export const hasContactUpdateValues = (form) => CONTACT_UPDATE_FIELDS.some(
    (field) => (form?.[field.key] ?? '').trim() !== ''
);

export const updateClientContact = (unidades, form) => fetchFlask(
    CONTACT_UPDATE_API,
    JSON.stringify(CONTACT_UPDATE_FIELDS.reduce(
        (payload, field) => ({ ...payload, [field.key]: (form?.[field.key] ?? '').trim() }),
        { unidades }
    ))
);

export const PORTAL_PASSWORD_API = 'alterarSenha';
export const PORTAL_EMAIL_API = 'alterarEmail';
export const PORTAL_COPY_USER_API = 'userFakePortal';

export const updatePortalPassword = (clienteId) => fetchFlask(
    PORTAL_PASSWORD_API,
    JSON.stringify({ clienteID: clienteId })
);

export const updatePortalEmail = (clienteId, email) => fetchFlask(
    PORTAL_EMAIL_API,
    JSON.stringify({ clienteID: clienteId, email: (email ?? '').trim() })
);

export const copyPortalUser = (clienteId) => fetchFlask(
    PORTAL_COPY_USER_API,
    JSON.stringify({ cliente_id: Number(clienteId) })
);

export const RECEIVABLE_CANCEL_API = 'cancelarOS';
export const RECEIVABLE_EXTEND_API = 'prorrogarBoleto';

export const RECEIVABLE_CANCEL_REASONS = [
    { value: 'ERRO DO OMIE', label: 'Erro do Omie' },
    { value: 'CLIENTE PAGOU FATURA NEOENERGIA', label: 'Cliente pagou fatura Neoenergia' },
    { value: 'COBRANÇA DE JUROS E MULTAS', label: 'Cobrança de juros e multas' },
    { value: 'VALOR ERRADO', label: 'Valor errado' },
    { value: 'COBRANÇA EM DUPLICIDADE', label: 'Cobrança em duplicidade' },
    { value: 'CPF/CNPJ ERRADO', label: 'CPF ou CNPJ errado' },
    { value: 'VENCIMENTO ERRADO', label: 'Vencimento errado' },
    { value: 'TROCA DE TITULARIDADE INDEVIDA', label: 'Troca de titularidade indevida' },
    { value: 'ERRO COMERCIAL', label: 'Erro comercial' },
    { value: 'ERRO DE TRATATIVA - PÓS-VENDA', label: 'Erro de tratativa - Pós-Venda' }
];

export const cancelReceivable = (codigoLancamento, obs) => fetchFlask(
    RECEIVABLE_CANCEL_API,
    JSON.stringify({ codigo_lancamento: codigoLancamento, obs })
);

export const extendReceivable = (codigoLancamento, vencimento) => fetchFlask(
    RECEIVABLE_EXTEND_API,
    JSON.stringify({ codigo_lancamento: codigoLancamento, vencimento: formatIsoDate(vencimento) })
);

export const UNIT_DEACTIVATE_API = 'desativarCC';

export const deactivateClientUnit = (unit) => fetchFlask(
    UNIT_DEACTIVATE_API,
    JSON.stringify({ cc: unit?.cc })
);

export const CLIENT_DISTRACT_API = 'distrato';

export const getDistractUnitNames = (units) => [...new Set(
    (units ?? [])
        .filter((unit) => !unit.suspenso && unit.unidade)
        .map((unit) => unit.unidade)
)];

export const getDistractDocuments = (leadDocument, contacts) => [...new Set(
    [leadDocument, ...(contacts?.contatos ?? []).map((contato) => contato.cpf_cnpj)]
        .map((document) => maskCpfCnpj(String(document ?? '').trim()))
        .filter(Boolean)
)];

export const distractClient = ({
    clienteId,
    cliente,
    unidades,
    data,
    motivo,
    cpfCnpj,
    representativeName = '',
    representativeCpf = ''
}) => fetchFlask(
    CLIENT_DISTRACT_API,
    JSON.stringify({
        unidades,
        cliente,
        cliente_id: clienteId,
        data: formatIsoDate(data),
        motivo,
        cpf_cnpj: cpfCnpj,
        representative_name: representativeName,
        representative_cpf: representativeCpf
    })
);

export const EMAIL_VERIFICATION_API = 'codigoVerificacaoEmail';

export const sendEmailVerificationCode = (email) => fetchFlask(
    EMAIL_VERIFICATION_API,
    JSON.stringify({ email: (email ?? '').trim() })
);
